import pouchdb from "pouchdb";
import { defineStore } from "pinia";
import { LOGGER } from "@/util/logger";
import pouchdbFind from "pouchdb-find";
import { useUserStore } from "@/stores/userStore";
import { ref, computed } from "vue";

export const useEDocsIDBStore = defineStore("eDocsIDB", () => {
  const userStore = useUserStore();
  pouchdb.plugin(pouchdbFind);

  const dbName = computed(() => {
    const userName = userStore.loggedInUser?.sub;
    return userName ? `bms-${userName.toLowerCase()}-eDocs` : "";
  });

  let eDocsdb = ref(null);
  const initDB = async () => {
    eDocsdb.value = pouchdb(dbName.value, {
      revs_limit: 0,
      auto_compaction: true,
    });
  };

  const saveAttachmentsIndividually = async (brkey, payload) => {
    if (payload._attachments && Object.keys(payload._attachments).length > 0) {
      for (const [attachmentName, attachmentData] of Object.entries(payload._attachments) ?? []) {
        const attachmentKey = `${brkey}-attachment-${attachmentName}`;
        try {
          let existingDoc = await eDocsdb.value.get(attachmentKey);
          await eDocsdb.value.put({
            _id: attachmentKey,
            _rev: existingDoc._rev,
            brkey: brkey,
            attachmentName: attachmentName,
            _attachments: {
              [attachmentName]: attachmentData,
            },
          });
        } catch (e) {
          // Document doesn't exist, create new one
          if (e.name === "not_found") {
            await eDocsdb.value.put({
              _id: attachmentKey,
              brkey: brkey,
              attachmentName: attachmentName,
              _attachments: {
                [attachmentName]: attachmentData,
              },
            });
          } else {
            throw e;
          }
        }
      }
    }
  };

  const saveEDocsIndividually = async (brkey, payload) => {
    if (payload.EDocs && Array.isArray(payload.EDocs)) {
      for (const edoc of payload.EDocs ?? []) {
        const edocKey = `${brkey}-edoc-${edoc.docContainerSeqNum}`;
        try {
          let existingEdoc = await eDocsdb.value.get(edocKey);
          await eDocsdb.value.put({
            _id: edocKey,
            _rev: existingEdoc._rev,
            brkey: brkey,
            ...edoc,
          });
        } catch (e) {
          // Document doesn't exist, create new one
          if (e.name === "not_found") {
            await eDocsdb.value.put({
              _id: edocKey,
              brkey: brkey,
              ...edoc,
            });
          } else {
            throw e;
          }
        }
      }
    }
  };

  const saveEDocsAndAttachments = async (payload) => {
    const brkey = payload.structureBridge.brkey + "";

    try {
      // Save each attachment individually with brkey-attachment-{name} as key
      await saveAttachmentsIndividually(brkey, payload);
      // Save each EDoc individually with brkey-docContainerSeqNum as key
      await saveEDocsIndividually(brkey, payload);
    } catch (e) {
      let error = Object.assign(e, {
        method: "saveEDocsAndAttachments",
        payload,
      });
      LOGGER.logException(error);
    }
  };

  const appendAttachments = (savedStructure, payload) => {
    const savedAttachmentsSize = savedStructure._attachments ? Object.keys(savedStructure._attachments)?.length : 0;
    const newAttachmentsSize = payload._attachments ? Object.keys(payload._attachments)?.length : 0;

    if (savedAttachmentsSize > 0 && newAttachmentsSize > 0) {
      return {
        attachments: {
          ...savedStructure._attachments,
          ...payload._attachments,
        },
      };
    } else if (savedAttachmentsSize > 0) {
      return {
        attachments: savedStructure._attachments,
      };
    } else if (newAttachmentsSize > 0) {
      return {
        attachments: payload._attachments,
      };
    } else {
      return {
        attachments: null,
      };
    }
  };

  const getAttachmentsFromIDB = async (brkey) => {
    try {
      const brkeyStr = brkey + "";
      let result = {
        _attachments: {},
        EDocs: [],
      };

      // Get all attachments by brkey using key prefix
      try {
        const attachmentPrefix = `${brkeyStr}-attachment-`;
        const attachmentsResult = await eDocsdb.value.allDocs({
          include_docs: true,
          attachments: true,
          startkey: attachmentPrefix,
          endkey: attachmentPrefix + "\uffff",
        });

        for (const row of attachmentsResult.rows) {
          if (row.doc._attachments) {
            Object.assign(result._attachments, row.doc._attachments);
          }
        }
      } catch (e) {
        if (e.name !== "not_found") {
          throw e;
        }
      }

      // Get all EDocs by brkey using key prefix
      try {
        const edocPrefix = `${brkeyStr}-edoc-`;
        const edocsResult = await eDocsdb.value.allDocs({
          include_docs: true,
          startkey: edocPrefix,
          endkey: edocPrefix + "\uffff",
        });
        result.EDocs = edocsResult.rows.map((row) => {
          const { ...edocData } = row.doc;
          return edocData;
        });
      } catch (e) {
        if (e.name !== "not_found") {
          throw e;
        }
      }
      return result;
    } catch (e) {
      let error = Object.assign(e, {
        method: "getAttachmentsFromIDB",
        brkey,
      });
      LOGGER.logException(error);
      return null;
    }
  };

  const deleteEDocsFromIDB = async (brKey) => {
    try {
      const brkeyStr = brKey + "";
      const docsToDelete = [];

      // Find all attachments with brkey prefix
      try {
        const attachmentPrefix = `${brkeyStr}-attachment-`;
        const attachmentsResult = await eDocsdb.value.allDocs({
          include_docs: true,
          startkey: attachmentPrefix,
          endkey: attachmentPrefix + "\uffff",
        });
        docsToDelete.push(...attachmentsResult.rows.map((row) => row.doc));
      } catch (e) {
        if (e.name !== "not_found") {
          throw e;
        }
      }

      // Find all edocs with brkey prefix
      try {
        const edocPrefix = `${brkeyStr}-edoc-`;
        const edocsResult = await eDocsdb.value.allDocs({
          include_docs: true,
          startkey: edocPrefix,
          endkey: edocPrefix + "\uffff",
        });
        docsToDelete.push(...edocsResult.rows.map((row) => row.doc));
      } catch (e) {
        if (e.name !== "not_found") {
          throw e;
        }
      }

      // Delete all found documents
      if (docsToDelete.length > 0) {
        const deleteDocs = docsToDelete.map((doc) => ({
          ...doc,
          _deleted: true,
        }));
        await eDocsdb.value.bulkDocs(deleteDocs);
      }
    } catch (e) {
      let error = Object.assign(e, {
        method: "deleteEDocsFromIDB",
        brKey,
      });
      LOGGER.logException(error);
    }
  };

  return {
    saveEDocsAndAttachments,
    appendAttachments,
    getAttachmentsFromIDB,
    initDB,
    deleteEDocsFromIDB,
  };
});
